import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout/page-header/PageHeader";
import { FieldGroup } from "@/v2/components/fields/field-group/FieldGroup";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { Pencil2Icon, ArrowLeftIcon } from "@radix-ui/react-icons";
import { Textarea } from "@/components/ui/textarea";
import { useApi } from "@/api/useApi";
import { useContext } from "react";
import { FeatureAccessContext } from "@/context/featureAccess";
import { useMutation, useQuery } from "react-query";
import { toast } from "sonner";
import { useSkillApi } from "@/v2/entities/skill/Skill.api";
import { buildSkillSlug, Skill } from "@/v2/entities/skill/Skill.model";
import { params } from "@/params";
import AiProgramming from "@/v2/assets/icons/AiProgramming";
import { JSONSchema7 } from "json-schema";
import {
  EditableTable,
  EditableTableField,
  EditableTableRow,
} from "@/v2/components/editable-table";
import styles from "./CreateSnowflakeSkill.module.scss";

export function CreateSnowflakeSkill() {
  const navigate = useNavigate();
  const location = useLocation();
  const { skillId } = useParams<{ skillId?: string }>();

  const isEditMode = !!skillId;
  const skillApi = useSkillApi();

  const { data: existingSkill, isLoading: isLoadingSkill } = useQuery({
    queryKey: ["skill", skillId],
    queryFn: () => skillApi.getSkill(skillId!),
    enabled: isEditMode && !!skillId,
  });

  const pathParts = useMemo(() => {
    const path = location.pathname;
    const basePath = "/automation-center/skills/create/snowflake";
    if (path.startsWith(basePath)) {
      const rest = path.slice(basePath.length);
      const parts = rest.split("/").filter(Boolean).map(decodeURIComponent);
      return {
        name: parts[0] || "",
        slug: parts[1] || "",
      };
    }
    return { name: "", slug: "" };
  }, [location.pathname]);

  const { name: nameParam, slug: slugParam } = pathParts;
  const { api } = useApi();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { createSkill, updateSkill } = useSkillApi();

  const [skillName, setSkillName] = useState(nameParam || "");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState("");
  const [slug, setSlug] = useState(
    slugParam && slugParam.startsWith("snowflake_")
      ? slugParam
      : slugParam
      ? `snowflake_${slugParam}`
      : ""
  );
  const [warehouse, setWarehouse] = useState("wh_business");
  const [parametersSchema, setParametersSchema] = useState<JSONSchema7>({
    type: "object",
    properties: {},
    required: [],
  });

  useEffect(() => {
    if (existingSkill) {
      setSkillName(existingSkill.name);
      setDescription(existingSkill.description);
      setSlug(
        existingSkill.slug.startsWith("snowflake_")
          ? existingSkill.slug
          : `snowflake_${existingSkill.slug}`
      );

      const params = existingSkill.parameters;
      if (params && typeof params === "object" && "properties" in params) {
        const properties = params.properties || {};

        if (properties.statement && typeof properties.statement === "object") {
          const statementProp = properties.statement as JSONSchema7;
          if (
            statementProp.default &&
            typeof statementProp.default === "string"
          ) {
            setQuery(statementProp.default);
          }
        }

        if (properties.warehouse && typeof properties.warehouse === "object") {
          const warehouseProp = properties.warehouse as JSONSchema7;
          if (
            warehouseProp.default &&
            typeof warehouseProp.default === "string"
          ) {
            setWarehouse(warehouseProp.default);
          }
        }

        const customParams: Record<string, JSONSchema7> = {};
        Object.entries(properties).forEach(([key, prop]) => {
          if (
            key !== "statement" &&
            key !== "warehouse" &&
            typeof prop === "object" &&
            prop !== null
          ) {
            customParams[key] = prop as JSONSchema7;
          }
        });

        setParametersSchema({
          type: "object",
          properties: customParams,
          required: (params.required || []).filter(
            (req: string) => req !== "statement" && req !== "warehouse"
          ) as string[],
        });
      }
    }
  }, [existingSkill]);

  const parameterFields: EditableTableField[] = [
    {
      key: "key",
      label: "Key",
      type: "text",
      placeholder: "Parameter key (e.g., orderId)",
      required: true,
    },
    {
      key: "description",
      label: "Description",
      type: "text",
      placeholder: "Description (optional)",
      required: false,
    },
    {
      key: "default",
      label: "Default Value",
      type: "text",
      placeholder: "Default value (optional)",
      required: false,
    },
  ];

  const parametersTableValue: EditableTableRow[] = useMemo(() => {
    const properties = parametersSchema.properties || {};
    return Object.entries(properties).map(([key, prop]) => {
      const property: JSONSchema7 =
        typeof prop === "object" &&
        prop !== null &&
        !Array.isArray(prop) &&
        typeof prop !== "boolean"
          ? (prop as JSONSchema7)
          : ({ type: "string" } as JSONSchema7);
      return {
        id: `param_${key}`,
        key,
        description: property.description || "",
        default: property.default ?? "",
        enabled: true,
      };
    });
  }, [parametersSchema]);

  const handleParametersTableChange = useCallback(
    (newRows: EditableTableRow[]) => {
      const enabledParams = newRows.filter(
        (row) => row.enabled && row.key && row.key.trim() !== ""
      );

      const newParamsSchema: JSONSchema7 = {
        type: "object",
        properties: {},
        required: [],
      };

      enabledParams.forEach((row) => {
        newParamsSchema.properties![row.key] = {
          type: "string",
          description: row.description || "",
          default: row.default || "",
        } as JSONSchema7;
        newParamsSchema.required!.push(row.key);
      });

      setParametersSchema(newParamsSchema);
    },
    [setParametersSchema]
  );

  useEffect(() => {
    if (skillName && !slug) {
      const baseSlug = buildSkillSlug(skillName);
      setSlug(`snowflake_${baseSlug}`);
    }
  }, [skillName, slug]);

  const handleSlugChange = useCallback(
    (newValue: string) => {
      const prefix = "snowflake_";
      if (newValue.startsWith(prefix)) {
        setSlug(newValue);
      } else if (newValue.length < prefix.length) {
        setSlug(prefix);
      } else {
        setSlug(`${prefix}${newValue.replace(/^snowflake_?/, "")}`);
      }
    },
    [setSlug]
  );

  const { mutateAsync: testQuery, isLoading: isTesting } = useMutation({
    mutationFn: async (requestBody: {
      statement: string;
      account?: string;
      role?: string;
      warehouse?: string;
      database?: string;
      schema?: string;
    }) => {
      if (!selectedWorkspace) {
        throw new Error("Workspace not selected");
      }
      const response = await api.post(
        `platform/auth-gateway-proxy/snowflake-jwt/api/v2/statements`,
        requestBody
      );
      return response.data;
    },
  });

  const replaceParametersInQuery = useCallback(
    (sqlQuery: string, schema: JSONSchema7): string => {
      let result = sqlQuery;
      const properties = schema.properties || {};

      for (const [key, prop] of Object.entries(properties)) {
        if (typeof prop === "object" && prop !== null && !Array.isArray(prop)) {
          const defaultValue = prop.default ?? "";
          const placeholder = `{${key}}`;
          result = result.replace(
            new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"),
            String(defaultValue)
          );
        }
      }

      return result;
    },
    []
  );

  const { mutateAsync: createSkillMutation, isLoading: isCreating } =
    useMutation({
      mutationFn: createSkill,
    });

  const { mutateAsync: updateSkillMutation, isLoading: isUpdating } =
    useMutation({
      mutationFn: updateSkill,
    });

  const handleTest = useCallback(async () => {
    if (!query.trim()) {
      toast.error("Please enter a SQL query");
      return;
    }

    try {
      const queryWithDefaults = replaceParametersInQuery(
        query,
        parametersSchema
      );
      await testQuery({
        statement: queryWithDefaults,
        warehouse: warehouse.trim() || undefined,
      });
      toast.success("Query executed successfully");
    } catch (error: any) {
      toast.error("Query failed", {
        description:
          error?.response?.data?.message || error?.message || "Unknown error",
      });
    }
  }, [query, parametersSchema, warehouse, testQuery, replaceParametersInQuery]);

  const handleCreate = useCallback(async () => {
    if (!skillName.trim() || !description.trim() || !query.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!selectedWorkspace) {
      toast.error("Workspace not selected");
      return;
    }

    const requestBodyJson = `{
    "statement": {statement},
    "warehouse": {warehouse}
  }`;

    const curl = `curl -X POST "${
      params.API_URL
    }/auth-gateway/proxy/snowflake-jwt/${selectedWorkspace}/api/v2/statements" \\
  -H "x-api-key: ${params.INTERNAL_TOKEN}" \\
  -H "Content-Type: application/json" \\
  -d '${requestBodyJson.replace(/\n/g, "\n  ")}'`;

    const finalParametersSchema: JSONSchema7 = {
      type: "object",
      properties: {
        statement: {
          type: "string",
          description: "SQL query to execute",
          default: query,
        },
        warehouse: {
          type: "string",
          description: "Snowflake warehouse",
          default: warehouse.trim() || "",
        } as JSONSchema7,
        ...parametersSchema.properties,
      },
      required: [
        "statement",
        "warehouse",
        ...(parametersSchema.required || []),
      ],
    };

    try {
      if (isEditMode && skillId) {
        await updateSkillMutation({
          id: skillId,
          name: skillName,
          description,
          curl,
          parameters: finalParametersSchema,
        });
        toast.success("Skill updated successfully");
      } else {
        await createSkillMutation({
          name: skillName,
          description,
          curl,
          slug: slug || `snowflake_${buildSkillSlug(skillName)}`,
          parameters: finalParametersSchema,
        });
        toast.success("Skill created successfully");
      }
      navigate("/automation-center/skills");
    } catch (error: any) {
      toast.error(
        isEditMode ? "Failed to update skill" : "Failed to create skill",
        {
          description:
            error?.response?.data?.message || error?.message || "Unknown error",
        }
      );
    }
  }, [
    skillName,
    description,
    query,
    slug,
    warehouse,
    parametersSchema,
    selectedWorkspace,
    createSkillMutation,
    updateSkillMutation,
    isEditMode,
    skillId,
    navigate,
  ]);

  const canCreate = useMemo(() => {
    return (
      skillName.trim() !== "" &&
      description.trim() !== "" &&
      query.trim() !== "" &&
      selectedWorkspace !== ""
    );
  }, [skillName, description, query, selectedWorkspace]);

  const isLoading = isLoadingSkill;
  const isSaving = isCreating || isUpdating;

  if (isLoading) {
    return (
      <PageLayoutV2>
        <Flex
          direction="column"
          height="100%"
          gap="4"
          px="6"
          py="4"
          align="center"
          justify="center"
        >
          <Text>Loading skill...</Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column" height="100%" gap="4" px="6" py="4">
        <Flex pl="6" pt="2">
          <Button
            variant="soft"
            color="gray"
            onClick={() => navigate("/automation-center/skills")}
          >
            <ArrowLeftIcon />
            Back
          </Button>
        </Flex>

        <PageHeader
          title={
            isEditMode
              ? "Edit Snowflake SQL Query Skill"
              : "Create Snowflake SQL Query Skill"
          }
          icon={<AiProgramming size={24} fill="orange" />}
        />

        <div className={styles.formContainer}>
          <div className={styles.contentWrapper}>
            <Flex width="100%" gap="4">
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <Text size="3" weight="bold" className={styles.sectionTitle}>
                    Skill Metadata
                  </Text>
                  <Text
                    size="2"
                    color="gray"
                    className={styles.sectionDescription}
                  >
                    Basic information about your skill
                  </Text>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.metadataFields}>
                    <FieldGroup
                      label="Name"
                      noSeparator
                      classes={{ wrapper: "p-0", root: "!gap-1" }}
                    >
                      <InlineTextField
                        size="2"
                        value={skillName}
                        onChange={setSkillName}
                        icon={<Pencil2Icon className="ml-2" />}
                        placeholder="Enter skill name"
                      />
                    </FieldGroup>

                    <FieldGroup
                      label="Slug"
                      noSeparator
                      classes={{ wrapper: "p-0", root: "!gap-1" }}
                    >
                      <InlineTextField
                        size="2"
                        value={slug}
                        onChange={handleSlugChange}
                        icon={<Pencil2Icon className="ml-2" />}
                        placeholder="snowflake_..."
                      />
                    </FieldGroup>

                    <FieldGroup
                      label="Description"
                      noSeparator
                      classes={{ wrapper: "p-0", root: "!gap-1" }}
                    >
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe what this skill does and when to use it..."
                        className={styles.descriptionField}
                      />
                    </FieldGroup>
                  </div>
                </div>
              </div>

              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <Text size="3" weight="bold" className={styles.sectionTitle}>
                    Connection Configuration
                  </Text>
                  <Text
                    size="2"
                    color="gray"
                    className={styles.sectionDescription}
                  >
                    Snowflake connection settings
                  </Text>
                </div>
                <div className={styles.sectionContent}>
                  <div className={styles.metadataFields}>
                    <FieldGroup
                      label="Warehouse"
                      noSeparator
                      classes={{ wrapper: "p-0", root: "!gap-1" }}
                    >
                      <InlineTextField
                        size="2"
                        value={warehouse}
                        onChange={setWarehouse}
                        icon={<Pencil2Icon className="ml-2" />}
                        placeholder="e.g., wh_business"
                      />
                    </FieldGroup>
                  </div>
                </div>
              </div>
            </Flex>

            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <Text size="3" weight="bold" className={styles.sectionTitle}>
                  Query Configuration
                </Text>
                <Text
                  size="2"
                  color="gray"
                  className={styles.sectionDescription}
                >
                  Define the SQL query and its parameters
                </Text>
              </div>
              <div className={styles.sectionContent}>
                <div className={styles.queryConfigRow}>
                  <div className={styles.sqlEditorWrapper}>
                    <Text size="2" weight="medium" mb="2">
                      SQL Query
                    </Text>
                    <Text size="1" color="gray" mb="3">
                      Enter the SQL query that will be executed when this skill
                      is called. Use {"{"}paramName{"}"} to reference
                      parameters.
                    </Text>
                    <Textarea
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={`-- Use {paramName} to reference parameters

-- Example:
-- SELECT * FROM orders WHERE id = '{orderId}' AND status = '{status}'

-- Your query:`}
                      className={styles.sqlEditor}
                      rows={16}
                    />
                  </div>

                  <div className={styles.parametersWrapper}>
                    <Text size="2" weight="medium" mb="2">
                      Parameters
                    </Text>
                    <Text size="1" color="gray" mb="3">
                      Define parameters that can be used in the SQL query using{" "}
                      {"{"}paramName{"}"} syntax.
                    </Text>
                    <EditableTable
                      value={parametersTableValue}
                      onChange={handleParametersTableChange}
                      fields={parameterFields}
                      title="Parameters"
                      addButtonText="Add Parameter"
                      emptyStateText="No parameters added yet"
                      showToggle={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Flex>
      <div className={styles.stickyFooter}>
        <Button
          variant="soft"
          color="gray"
          onClick={() => navigate("/automation-center/skills")}
        >
          Cancel
        </Button>
        <div className={styles.footerActions}>
          <Button
            variant="soft"
            color="gray"
            onClick={handleTest}
            loading={isTesting}
            disabled={!query.trim() || !selectedWorkspace}
          >
            Test Query
          </Button>
          <Button
            variant="solid"
            color="gray"
            highContrast
            onClick={handleCreate}
            loading={isSaving}
            disabled={!canCreate || isLoading}
          >
            {isEditMode ? "Update Skill" : "Create Skill"}
          </Button>
        </div>
      </div>
    </PageLayoutV2>
  );
}
